import { ChevronDown } from "lucide-react";
import { createContext, type FormEvent, type ReactNode, useContext, useMemo, useRef, useState } from "react";
import { Link } from "react-router";
import { toast } from "sonner";
import { ApiError } from "@/api/client";
import {
  useAccounts,
  useCategories,
  useCreateTransaction,
  useDeleteTransaction,
  useTransactionPages,
} from "@/api/finance";
import type { Category, CategoryKind, Transaction } from "@/api/types";
import { CategoryIcon } from "@/components/CategoryIcon";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Drawer, DrawerContent, DrawerTitle } from "@/components/ui/drawer";
import { ErrorBanner, Loading, ResendWarning } from "@/components/ui/feedback";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { currentBreakpoint, useBreakpoint } from "@/lib/breakpoint";
import { todayCivil } from "@/lib/dates";
import { useIdempotencyKey } from "@/lib/idempotency";
import { formatMoney, parseMoneyInput } from "@/lib/money";
import { cn } from "@/lib/utils";
import { useWorkspace } from "./workspace-context";

const QuickAddContext = createContext<(() => void) | null>(null);

/** Abre o lançamento rápido (o "+" do shell). */
export function useOpenQuickAdd() {
  const open = useContext(QuickAddContext);
  if (!open) throw new Error("useOpenQuickAdd fora do QuickAddProvider");
  return open;
}

const lastAccountKey = (wid: string) => `nora:lastAccount:${wid}`;
function readLastAccount(wid: string) {
  try {
    return localStorage.getItem(lastAccountKey(wid));
  } catch {
    return null;
  }
}
function writeLastAccount(wid: string, id: string) {
  try {
    localStorage.setItem(lastAccountKey(wid), id);
  } catch {
    // sem armazenamento: só perde a conveniência
  }
}

/** Painel do lançamento rápido: drawer no celular, diálogo no tablet/desktop. Devolve o foco a quem abriu. */
export function QuickAddProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const opener = useRef<HTMLElement | null>(null);
  const breakpoint = useBreakpoint();
  // O formato (drawer/diálogo) é fixado ao abrir: girar o tablet com o painel aberto não remonta o formulário.
  const [mobileAtOpen, setMobileAtOpen] = useState(breakpoint === "mobile");
  const mobile = open ? mobileAtOpen : breakpoint === "mobile";
  const value = useMemo(
    () => () => {
      opener.current = document.activeElement as HTMLElement | null;
      setMobileAtOpen(currentBreakpoint() === "mobile");
      setOpen(true);
    },
    [],
  );
  const restoreFocus = (e: Event) => {
    e.preventDefault();
    opener.current?.focus();
  };
  const form = open && <QuickAddForm onDone={() => setOpen(false)} />;

  return (
    <QuickAddContext.Provider value={value}>
      {children}
      {mobile ? (
        <Drawer open={open} onOpenChange={setOpen} repositionInputs>
          <DrawerContent aria-describedby={undefined} onCloseAutoFocus={restoreFocus}>
            <div className="overflow-y-auto px-4 pb-4">
              <DrawerTitle className="mb-4">Novo lançamento</DrawerTitle>
              {form}
            </div>
          </DrawerContent>
        </Drawer>
      ) : (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent aria-describedby={undefined} onCloseAutoFocus={restoreFocus}>
            <div className="overflow-y-auto p-6">
              <DialogTitle className="mb-4">Novo lançamento</DialogTitle>
              {form}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </QuickAddContext.Provider>
  );
}

const KINDS: Array<{ value: CategoryKind; label: string; save: string; done: string }> = [
  { value: "expense", label: "Despesa", save: "Salvar despesa", done: "Despesa" },
  { value: "income", label: "Receita", save: "Salvar receita", done: "Receita" },
];

/** Categorias do tipo (sem arquivadas), as mais usadas nos lançamentos recentes primeiro; empate → nome. */
function rankCategories(categories: Category[], kind: CategoryKind, recent: Transaction[]) {
  const uses = new Map<string, number>();
  for (const t of recent) if (t.categoryId) uses.set(t.categoryId, (uses.get(t.categoryId) ?? 0) + 1);
  return categories
    .filter((c) => c.kind === kind && !c.archivedAt)
    .sort((a, b) => (uses.get(b.id) ?? 0) - (uses.get(a.id) ?? 0) || a.name.localeCompare(b.name, "pt-BR"));
}

function QuickAddForm({ onDone }: { onDone: () => void }) {
  const ws = useWorkspace();
  const accounts = useAccounts(ws.id);
  const categories = useCategories(ws.id);
  const recent = useTransactionPages(ws.id, {}, 50);
  const create = useCreateTransaction(ws.id);
  const remove = useDeleteTransaction(ws.id);

  const [amount, setAmount] = useState("");
  const [kind, setKind] = useState<CategoryKind>("expense");
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [accountId, setAccountId] = useState<string | null>(null);
  const [date, setDate] = useState(() => todayCivil(ws.timezone));
  const [more, setMore] = useState(false);
  const [description, setDescription] = useState("");
  const [pending, setPending] = useState(false);
  const [tags, setTags] = useState("");
  const [notes, setNotes] = useState("");
  const [invalid, setInvalid] = useState<string | null>(null);
  const [confirmResend, setConfirmResend] = useState(false);

  const ranked = useMemo(
    () => rankCategories(categories.data ?? [], kind, recent.data?.pages[0]?.transactions ?? []),
    [categories.data, kind, recent.data],
  );
  const activeAccounts = accounts.data ?? [];
  const [last] = useState(() => readLastAccount(ws.id));
  const account =
    activeAccounts.find((a) => a.id === accountId) ??
    activeAccounts.find((a) => a.id === last) ??
    activeAccounts[0];
  const category = ranked.find((c) => c.id === categoryId) ?? ranked[0];
  const meta = KINDS.find((k) => k.value === kind) ?? (KINDS[0] as (typeof KINDS)[number]);
  const idempotency = useIdempotencyKey({
    amount,
    kind,
    category: category?.id,
    account: account?.id,
    date,
    description,
    pending,
    tags,
    notes,
  });

  if (accounts.isPending || categories.isPending) return <Loading />;
  // Falha ao carregar não é workspace vazio: mostra o erro e deixa tentar de novo.
  const failed = [accounts, categories].filter((q) => q.isError);
  if (failed.length)
    return (
      <div className="flex flex-col items-start gap-3 py-2">
        <ErrorBanner error={failed[0]?.error} />
        <Button
          variant="secondary"
          disabled={failed.some((q) => q.isFetching)}
          onClick={() => failed.forEach((q) => void q.refetch())}
        >
          Tentar de novo
        </Button>
      </div>
    );
  if (activeAccounts.length === 0)
    return (
      <div className="flex flex-col items-start gap-3 py-2">
        <p className="font-medium">Crie uma conta antes de lançar.</p>
        <Button asChild variant="secondary">
          <Link to={`/w/${ws.id}/accounts`} onClick={onDone}>
            Ir para Contas
          </Link>
        </Button>
      </div>
    );

  async function submit(e: FormEvent) {
    e.preventDefault();
    const amountMinor = parseMoneyInput(amount);
    if (amountMinor === null) {
      setInvalid("Informe um valor válido, ex.: 12,34");
      return;
    }
    if (!account || !category) return;
    setInvalid(null);
    // P1: a resposta anterior se perdeu e o conteúdo mudou; avisa antes de enviar um pedido novo.
    if (idempotency.changedAfterFailure) {
      setConfirmResend(true);
      return;
    }
    await save(amountMinor, idempotency.key);
  }

  async function save(amountMinor: number, idempotencyKey: string) {
    // O que este envio leva: se a resposta se perder, a falha é dele, mesmo que o formulário mude no meio.
    const sent = { key: idempotencyKey, fingerprint: idempotency.fingerprint };
    if (!account || !category) return;
    const tagList = tags
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);
    let created: { transaction: Transaction };
    try {
      created = (await create.mutateAsync({
        type: kind,
        amountMinor,
        accountId: account.id,
        categoryId: category.id,
        date,
        description: description.trim() || category.name,
        status: pending ? "pending" : "cleared",
        ...(tagList.length ? { tags: tagList } : {}),
        ...(notes.trim() ? { notes: notes.trim() } : {}),
        idempotencyKey,
      })) as { transaction: Transaction };
    } catch (error) {
      if (error instanceof ApiError && error.code === "network_error") idempotency.markFailure(sent);
      throw error;
    }
    idempotency.rotate();
    writeLastAccount(ws.id, account.id);
    onDone();
    toast(`${meta.done} de ${formatMoney(amountMinor, account.currency)} salva`, {
      duration: 6000,
      action: {
        label: "Desfazer",
        onClick: () =>
          void remove
            .mutateAsync(created.transaction.id)
            .then(() => toast("Lançamento desfeito"))
            .catch(() => toast.error("Não deu para desfazer — exclua pela lista.")),
      },
    });
  }

  function confirmSave() {
    setConfirmResend(false);
    const amountMinor = parseMoneyInput(amount);
    if (amountMinor === null) {
      setInvalid("Informe um valor válido, ex.: 12,34");
      return;
    }
    // Se o conteúdo voltou ao da falha, a chave dela é reaproveitada (não duplica); senão, chave nova.
    const key = idempotency.changedAfterFailure ? idempotency.rotate() : idempotency.key;
    void save(amountMinor, key).catch(() => undefined);
  }

  return (
    <form onSubmit={(e) => void submit(e).catch(() => undefined)} className="flex flex-col gap-5">
      <div className="flex flex-col items-center gap-1">
        <Label htmlFor="qa-amount" className="text-muted">
          Valor
        </Label>
        <Input
          id="qa-amount"
          autoFocus
          inputMode="decimal"
          autoComplete="off"
          placeholder="0,00"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          aria-invalid={invalid ? true : undefined}
          className={cn(
            "min-h-16 border-0 bg-transparent text-center text-[40px] font-bold tracking-[-0.03em] tabular-nums",
            kind === "expense" ? "text-expense" : "text-income",
          )}
        />
        {invalid && (
          <p role="alert" className="text-sm text-expense">
            {invalid}
          </p>
        )}
      </div>

      <fieldset className="m-0 border-0 p-0">
        <legend className="sr-only">Tipo</legend>
        <div className="grid grid-cols-2 gap-1 rounded-chip bg-surface-2 p-1">
          {KINDS.map((k) => (
            <label
              key={k.value}
              className={cn(
                "flex min-h-11 cursor-pointer items-center justify-center rounded-control text-[15px] font-semibold",
                "has-focus-visible:outline-2 has-focus-visible:outline-accent",
                kind === k.value ? "bg-surface shadow-card" : "text-muted",
                kind === k.value && (k.value === "expense" ? "text-expense" : "text-income"),
              )}
            >
              <input
                type="radio"
                name="qa-kind"
                className="sr-only"
                checked={kind === k.value}
                onChange={() => {
                  setKind(k.value);
                  setCategoryId(null);
                }}
              />
              {k.label}
            </label>
          ))}
        </div>
      </fieldset>

      <fieldset className="m-0 min-w-0 border-0 p-0">
        <legend className="mb-2 text-sm font-medium">Categoria</legend>
        {ranked.length === 0 && (
          <p className="m-0 flex flex-wrap items-center gap-x-2 text-sm text-muted">
            <span>Nenhuma categoria de {kind === "expense" ? "despesa" : "receita"}.</span>
            <Link
              to={`/w/${ws.id}/categories`}
              onClick={onDone}
              className="font-medium text-link hover:underline"
            >
              Criar em Categorias
            </Link>
          </p>
        )}
        <div className="no-scrollbar -mx-1 flex gap-2 overflow-x-auto px-1 pb-1">
          {ranked.map((c) => (
            <label
              key={c.id}
              className={cn(
                "flex min-h-11 shrink-0 cursor-pointer items-center gap-2 rounded-full border py-1 pr-4 pl-1 text-sm font-medium whitespace-nowrap",
                "has-focus-visible:outline-2 has-focus-visible:outline-accent",
                category?.id === c.id
                  ? "border-ink bg-ink text-surface"
                  : "border-border bg-surface text-ink hover:bg-surface-2",
              )}
            >
              <input
                type="radio"
                name="qa-category"
                className="sr-only"
                checked={category?.id === c.id}
                onChange={() => setCategoryId(c.id)}
              />
              <CategoryIcon category={c} className="size-8 rounded-full" />
              {c.name}
            </label>
          ))}
        </div>
      </fieldset>

      <div className="grid grid-cols-2 gap-3">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="qa-account">Conta</Label>
          <select
            id="qa-account"
            value={account?.id}
            onChange={(e) => setAccountId(e.target.value)}
            className="min-h-12 rounded-chip border border-border bg-surface px-3 text-[15px] text-ink focus-visible:outline-2 focus-visible:outline-accent"
          >
            {activeAccounts.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="qa-date">Data</Label>
          <Input id="qa-date" type="date" required value={date} onChange={(e) => setDate(e.target.value)} />
        </div>
      </div>

      <div>
        <button
          type="button"
          aria-expanded={more}
          onClick={() => setMore((m) => !m)}
          className="flex min-h-11 items-center gap-1 text-sm font-medium text-link focus-visible:outline-2 focus-visible:outline-accent"
        >
          Mais detalhes
          <ChevronDown
            aria-hidden="true"
            className={cn("size-4 motion-safe:transition-transform", more && "rotate-180")}
          />
        </button>
        {more && (
          <div className="mt-2 flex flex-col gap-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="qa-description">Descrição</Label>
              <Input
                id="qa-description"
                maxLength={200}
                placeholder={category?.name}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="qa-tags">Tags (separadas por vírgula)</Label>
              <Input id="qa-tags" value={tags} onChange={(e) => setTags(e.target.value)} />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="qa-notes">Notas</Label>
              <textarea
                id="qa-notes"
                rows={2}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="rounded-chip border border-border bg-surface px-4 py-3 text-base text-ink focus-visible:outline-2 focus-visible:outline-accent"
              />
            </div>
            <label className="flex min-h-11 items-center gap-3 text-[15px]">
              <input
                type="checkbox"
                checked={pending}
                onChange={(e) => setPending(e.target.checked)}
                className="size-5 accent-accent"
              />
              Ainda não foi pago/recebido (pendente)
            </label>
          </div>
        )}
      </div>

      <ErrorBanner error={create.error} saving />
      {confirmResend && <ResendWarning onConfirm={confirmSave} onCancel={() => setConfirmResend(false)} />}
      <div className="sticky bottom-0 -mx-1 bg-surface px-1 pt-1 pb-1">
        <Button type="submit" size="lg" className="w-full" disabled={create.isPending || !category}>
          {meta.save}
        </Button>
      </div>
    </form>
  );
}
